// Timeline arithmetic of the gantt: dates as UTC day numbers, position ↔ date, snapping to a day, header bands and weekends.
// Every pixel is computed by one function (xOf) and rounded there, so the grid, the bars and the header never drift apart.

export const DAY = 86_400_000;
export type Zoom = 'day' | 'week' | 'month' | 'quarter';
export const ZOOMS: Zoom[] = ['day', 'week', 'month', 'quarter'];
/** Pixels per day: whole numbers for day and week, so every day line falls on a pixel. */
export const PX: Record<Zoom, number> = { day: 36, week: 16, month: 5, quarter: 2 };
export const ZOOM_LABEL: Record<Zoom, string> = { day: 'Дни', week: 'Недели', month: 'Месяцы', quarter: 'Кварталы' };

/** 'YYYY-MM-DD…' → UTC midnight in ms; anything else → null. */
export function dayOf(v: unknown): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v ?? ''));
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}
export const isoOf = (t: number) => new Date(t).toISOString().slice(0, 10);
export const todayUtc = (now = new Date()) => Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());

/** Start of the unit that holds `t`: Monday of the week, the 1st of the month, of the quarter, of the year. */
export function startOf(t: number, unit: 'week' | 'month' | 'quarter' | 'year'): number {
  const d = new Date(t);
  if (unit === 'week') return t - ((d.getUTCDay() + 6) % 7) * DAY;
  const m = d.getUTCMonth();
  return Date.UTC(d.getUTCFullYear(), unit === 'month' ? m : unit === 'quarter' ? m - (m % 3) : 0, 1);
}
export function addMonths(t: number, n: number): number {
  const d = new Date(t);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, d.getUTCDate());
}

export const xOf = (t: number, from: number, px: number) => Math.round(((t - from) / DAY) * px);
/** The day under pixel `x` (the left edge belongs to the day). */
export const dayAt = (x: number, from: number, px: number) => from + Math.floor(x / px) * DAY;
/** A pointer shift in pixels → whole days, rounded to the nearest day line the same way left and right. */
export const snapDays = (dx: number, px: number) => Math.sign(dx) * Math.round(Math.abs(dx) / px) || 0;

/** A bar covers its last day too: from the left edge of `start` to the right edge of `end`. */
export function barBox(start: number, end: number, from: number, px: number): { left: number; width: number } {
  const a = Math.min(start, end), b = Math.max(start, end);
  const left = xOf(a, from, px);
  return { left, width: Math.max(xOf(b + DAY, from, px) - left, 2) };
}

export type DragMode = 'move' | 'start' | 'end';
/** Applies a drag of `delta` days; an edge never passes the other one (a one-day bar stays one day). */
export function shiftRange(r: { start: number; end: number }, mode: DragMode, delta: number): { start: number; end: number } {
  const d = delta * DAY;
  if (mode === 'move') return { start: r.start + d, end: r.end + d };
  if (mode === 'start') return { start: Math.min(r.start + d, r.end), end: r.end };
  return { start: r.start, end: Math.max(r.end + d, r.start) };
}

/**
 * The visible range: the data and today with a margin, aligned to the unit of the header, long enough to fill `minDays`.
 * The margin keeps room to drag a bar past the first and the last task.
 */
export function timeRange(dates: number[], today: number, zoom: Zoom, minDays = 0): { from: number; to: number; days: number } {
  const lo = Math.min(today, ...dates), hi = Math.max(today, ...dates);
  const unit = zoom === 'day' || zoom === 'week' ? 'week' : zoom === 'month' ? 'month' : 'quarter';
  const pad = zoom === 'day' ? 14 : zoom === 'week' ? 28 : zoom === 'month' ? 60 : 120;
  const from = startOf(lo - pad * DAY, unit);
  let to = hi + pad * DAY;
  to = Math.max(to, from + (minDays - 1) * DAY);
  to = unit === 'week' ? startOf(to, 'week') + 6 * DAY : addMonths(startOf(to, unit), unit === 'month' ? 1 : 3) - DAY;
  return { from, to, days: Math.round((to - from) / DAY) + 1 };
}

export type Cell = { key: number; left: number; width: number; label: string; weekend?: boolean; today?: boolean };

const fmt = (t: number, o: Intl.DateTimeFormatOptions) => new Date(t).toLocaleDateString('ru-RU', { ...o, timeZone: 'UTC' });
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const ROMAN = ['I', 'II', 'III', 'IV'];

/** Splits [from, to] into cells starting at each boundary that `next` yields. */
function cells(from: number, to: number, px: number, first: number, next: (t: number) => number, label: (t: number) => string): Cell[] {
  const out: Cell[] = [];
  const end = to + DAY;
  for (let t = first; t < end; t = next(t)) {
    const a = Math.max(t, from), b = Math.min(next(t), end);
    const left = xOf(a, from, px);
    out.push({ key: t, left, width: xOf(b, from, px) - left, label: label(t) });
  }
  return out;
}

/** Two header bands: the upper names the larger unit, the lower holds the grid cells that the body lines follow. */
export function bands(from: number, to: number, zoom: Zoom, today: number): { top: Cell[]; bottom: Cell[] } {
  const px = PX[zoom];
  const day = (t: number) => t + DAY, week = (t: number) => t + 7 * DAY;
  const month = (t: number) => addMonths(startOf(t, 'month'), 1), quarter = (t: number) => addMonths(startOf(t, 'quarter'), 3);
  const year = (t: number) => Date.UTC(new Date(t).getUTCFullYear() + 1, 0, 1);
  const monthName = (t: number) => cap(fmt(t, { month: 'long', year: 'numeric' }).replace(/\s?г\.$/, ''));
  const yearName = (t: number) => String(new Date(t).getUTCFullYear());
  if (zoom === 'day') {
    const bottom = cells(from, to, px, from, day, (t) => String(new Date(t).getUTCDate()));
    for (const c of bottom) { const wd = new Date(c.key).getUTCDay(); c.weekend = wd === 0 || wd === 6; c.today = c.key === today; }
    return { top: cells(from, to, px, startOf(from, 'month'), month, monthName), bottom };
  }
  if (zoom === 'week') {
    const bottom = cells(from, to, px, startOf(from, 'week'), week, (t) => `${new Date(t).getUTCDate()} – ${new Date(t + 6 * DAY).getUTCDate()}`);
    for (const c of bottom) c.today = today >= c.key && today < c.key + 7 * DAY;
    return { top: cells(from, to, px, startOf(from, 'month'), month, monthName), bottom };
  }
  if (zoom === 'month') {
    const bottom = cells(from, to, px, startOf(from, 'month'), month, (t) => cap(fmt(t, { month: 'short' }).replace('.', '')));
    for (const c of bottom) c.today = startOf(today, 'month') === c.key;
    return { top: cells(from, to, px, startOf(from, 'year'), year, yearName), bottom };
  }
  const bottom = cells(from, to, px, startOf(from, 'quarter'), quarter, (t) => `${ROMAN[Math.floor(new Date(t).getUTCMonth() / 3)]} кв.`);
  for (const c of bottom) c.today = startOf(today, 'quarter') === c.key;
  return { top: cells(from, to, px, startOf(from, 'year'), year, yearName), bottom };
}

/** Saturday–Sunday strips, merged, for the zooms where a day is wide enough to see them. */
export function weekends(from: number, to: number, zoom: Zoom): { left: number; width: number }[] {
  if (zoom !== 'day' && zoom !== 'week') return [];
  const px = PX[zoom], out: { left: number; width: number }[] = [];
  for (let t = startOf(from, 'week') + 5 * DAY; t <= to; t += 7 * DAY) {
    const a = Math.max(t, from), b = Math.min(t + 2 * DAY, to + DAY);
    if (b > a) out.push({ left: xOf(a, from, px), width: xOf(b, from, px) - xOf(a, from, px) });
  }
  return out;
}

/** «3 нояб – 9 нояб · 7 дн.» for the hint under a dragged bar; a single day is one date. */
export function rangeLabel(start: number, end: number): string {
  const d = (t: number) => fmt(t, { day: 'numeric', month: 'short' }).replace('.', '');
  const n = Math.round((end - start) / DAY) + 1;
  return start === end ? d(start) : `${d(start)} – ${d(end)} · ${n} дн.`;
}

/** An orthogonal path through `pts` with corners rounded by `r` — the dependency line. */
export function roundedPath(pts: [number, number][], r = 4): string {
  if (pts.length < 2) return '';
  let d = `M${pts[0]![0]},${pts[0]![1]}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const [px0, py0] = pts[i - 1]!, [x, y] = pts[i]!, [nx, ny] = pts[i + 1]!;
    const inLen = Math.hypot(x - px0, y - py0), outLen = Math.hypot(nx - x, ny - y);
    const k = Math.min(r, inLen / 2, outLen / 2);
    if (k <= 0) { d += ` L${x},${y}`; continue; }
    const ax = x - (Math.sign(x - px0) * k), ay = y - (Math.sign(y - py0) * k);
    const bx = x + (Math.sign(nx - x) * k), by = y + (Math.sign(ny - y) * k);
    d += ` L${ax},${ay} Q${x},${y} ${bx},${by}`;
  }
  const last = pts[pts.length - 1]!;
  return `${d} L${last[0]},${last[1]}`;
}

/**
 * The route of a dependency from the end of one bar (x1, y1) to the start of the next (x2, y2): straight across with one step
 * when there is room, otherwise out to the right, along the gap between the rows, and back into the start.
 */
export function depRoute(x1: number, y1: number, x2: number, y2: number, row: number): [number, number][] {
  const gap = 10;
  if (y1 === y2 && x2 - x1 >= gap) return [[x1, y1], [x2, y2]];
  if (x2 - x1 >= gap * 2) { const mx = x1 + gap; return [[x1, y1], [mx, y1], [mx, y2], [x2, y2]]; }
  const between = y1 < y2 ? y2 - row / 2 : y2 + row / 2;
  return [[x1, y1], [x1 + gap, y1], [x1 + gap, between], [x2 - gap, between], [x2 - gap, y2], [x2, y2]];
}
